import { Link, useLocation } from '@remix-run/react'
import { useState } from 'react'
import {
  Collapse,
  Nav,
  Navbar,
  NavbarBrand,
  NavbarToggler,
  NavItem,
} from 'reactstrap'

type NavLinkItem =
  | { title: string; to: string }
  | {
      title: string
      href: string
    }

const BASE_NAV_LINKS = [
  { title: 'Início', to: '/' },
  { title: 'Contato', to: '/#contato' },
  {
    title: 'Loja',
    href: 'https://loja.clubedotennis.com.br',
  },
] as const satisfies readonly NavLinkItem[]

function contatoHref(location: ReturnType<typeof useLocation>): string {
  if (location.pathname.startsWith('/novidades')) {
    return `${location.pathname}${location.search}#contato`
  }
  return '/#contato'
}

function scrollToContatoId() {
  document.getElementById('contato')?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  })
}

export type NavbarLandingVariant = 'hero' | 'light'

type NavbarLandingProps = {
  variant?: NavbarLandingVariant
}

export default function NavbarLanding({ variant = 'hero' }: NavbarLandingProps) {
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const closeMenu = () => setMenuOpen(false)
  const isLight = variant === 'light'

  const navLinkItems: NavLinkItem[] = BASE_NAV_LINKS.map((item) =>
    item.title === 'Contato'
      ? { title: item.title, to: contatoHref(location) }
      : item,
  )

  const navClass =
    'landing-nav-links mobile-nav-links mb-0 flex-column flex-md-row align-items-start align-items-md-center'

  const onContatoClick = () => {
    closeMenu()
    if (!location.pathname.startsWith('/novidades')) return
    queueMicrotask(() => {
      scrollToContatoId()
    })
  }

  const lightLinks = (
    <>
      <NavbarToggler
        className="d-md-none ms-auto"
        onClick={() => setMenuOpen((o) => !o)}
      />
      <Collapse isOpen={menuOpen} navbar className="w-100 w-md-auto flex-md-grow-0">
        <Nav navbar className={navClass}>
          {navLinkItems.map((item) =>
            'href' in item ? (
              <NavItem key={item.title}>
                <a
                  href={item.href}
                  className="landing-nav-link"
                  onClick={closeMenu}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {item.title}
                </a>
              </NavItem>
            ) : (
              <NavItem key={item.title}>
                <Link
                  to={item.to}
                  className="landing-nav-link"
                  onClick={() => {
                    if (item.title === 'Contato') {
                      onContatoClick()
                      return
                    }
                    closeMenu()
                  }}
                >
                  {item.title}
                </Link>
              </NavItem>
            ),
          )}
        </Nav>
      </Collapse>
    </>
  )

  return (
    <Navbar
      expand="md"
      {...(!isLight ? { fixed: 'top' } : {})}
      className={
        isLight
          ? 'landing-navbar landing-navbar--light navbar-light border-0'
          : 'landing-navbar navbar-transparent navbar-light border-0'
      }
    >
      {isLight ? (
        <div className="container py-2">
          <div className="d-flex flex-wrap align-items-center w-100 column-gap-3 row-gap-2">
            <NavbarBrand tag="div" className="logo m-0 p-0">
              <Link to="/" onClick={closeMenu}>
                <img
                  className="logo-img"
                  src="/images/logo.svg"
                  alt="Clube do Tennis"
                />
              </Link>
            </NavbarBrand>
            {lightLinks}
          </div>
        </div>
      ) : (
        <div
          className="container d-flex align-items-center justify-content-between"
          style={{ padding: '10px 0' }}
        >
          <NavbarBrand tag="div" className="logo m-0 p-0">
            <Link to="/">
              <>
                <img
                  className="logo-img d-none d-md-block"
                  src="/images/logo-white.svg"
                  alt="Clube do Tennis"
                />
                <img
                  className="logo-img d-block d-md-none"
                  src="/images/lp/logo-black-cdt.png"
                  alt="Clube do Tennis"
                />
              </>
            </Link>
          </NavbarBrand>
        </div>
      )}
    </Navbar>
  )
}
